import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, Plus, Eye, EyeOff, Printer, Copy, Trash, CalendarDays, ChevronDown, ClipboardCheck, RefreshCw, Edit3 } from "lucide-react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/Card";
import { TableRowsSkeleton } from "../components/ui/Skeleton";
import { Button } from "../components/ui/Button";
import { Input, Select, Label } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import PrintPreview from "../components/ui/PrintPreview";
import { OrdersListPrint } from "../components/print/ReportTemplates";
import { toast } from "../components/ui/Toast";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { formatVND, formatDateTime, parseApiError} from "../lib/utils";
import { useSort } from "../hooks/useSort";
import { useFeature } from "../contexts/CreatorConfigContext";

// Hàm format ngày local (tránh lệch múi giờ UTC)
function fmtLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Hàm tính khoảng thời gian nhanh
function getQuickRange(key) {
  const now = new Date();
  const todayStr = fmtLocal(now);

  if (key === "today") {
    return { dateFrom: todayStr, dateTo: todayStr };
  }
  if (key === "week") {
    // Thứ 2 là đầu tuần
    const dow = now.getDay() === 0 ? 6 : now.getDay() - 1;
    const mon = new Date(now); mon.setDate(now.getDate() - dow);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { dateFrom: fmtLocal(mon), dateTo: fmtLocal(sun) };
  }
  if (key === "month") {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last  = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { dateFrom: fmtLocal(first), dateTo: fmtLocal(last) };
  }
  if (key === "year") {
    const first = new Date(now.getFullYear(), 0, 1);
    const last  = new Date(now.getFullYear(), 11, 31);
    return { dateFrom: fmtLocal(first), dateTo: fmtLocal(last) };
  }
  return { dateFrom: "", dateTo: "" };
}

export default function Orders() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canPrint     = useFeature("orders.print");
  const canDuplicate = useFeature("orders.duplicate");
  const canEdit      = useFeature("orders.edit");
  const canDelete    = useFeature("orders.delete");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [payment, setPayment] = useState("");
  const [type, setType] = useState("");
  const [dateFrom, setDateFrom] = useState(searchParams.get("date_from") || "");
  const [dateTo, setDateTo] = useState(searchParams.get("date_to") || "");
  const [quickRange, setQuickRange] = useState(""); // "today" | "week" | "month" | "year" | "custom" | ""
  const [sort, setSort] = useState("created_at");
  const [direction, setDirection] = useState("desc");
  const [loading, setLoading] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [delAllOpen, setDelAllOpen] = useState(false);
  const [delAllForm, setDelAllForm] = useState({ account_password: "", pin_a: "", pin_b: "", confirm_text: "" });
  const [showDelPw, setShowDelPw] = useState({ acc: false, a: false, b: false });
  const [delAllBusy, setDelAllBusy] = useState(false);

  // ── Chốt cuối ngày ──────────────────────────────────────────────────────────
  const [eodOpen, setEodOpen]         = useState(false);
  const [eodDate, setEodDate]         = useState(fmtLocal(new Date()));
  const [eodOrders, setEodOrders]     = useState([]);   // đơn trong ngày
  const [eodLoading, setEodLoading]   = useState(false);
  const [eodEdits, setEodEdits]       = useState({});   // { order_id: { payment_method, note, dirty } }
  const [eodSaving, setEodSaving]     = useState({});   // { order_id: true }
  const [eodNoteOpen, setEodNoteOpen] = useState(null); // order_id đang nhập ghi chú

  const PAY_LABEL = { cash: "Tiền mặt", transfer: "Chuyển khoản", ewallet: "Ví điện tử", card: "Thẻ", debt: "Công nợ" };
  const PAY_OPTIONS = Object.entries(PAY_LABEL);

  const loadEod = async (date) => {
    setEodLoading(true);
    try {
      const { data } = await api.get("/orders", {
        params: { date_from: date, date_to: date, sort: "created_at", direction: "asc" },
      });
      // Chỉ lấy đơn chưa huỷ
      const active = data.filter((o) => o.status !== "cancelled");
      setEodOrders(active);
      // Khởi tạo edits từ dữ liệu hiện tại
      const init = {};
      active.forEach((o) => { init[o.order_id] = { payment_method: o.payment_method, note: "", dirty: false }; });
      setEodEdits(init);
    } catch {
      toast({ title: "Lỗi tải dữ liệu", variant: "error" });
    } finally {
      setEodLoading(false);
    }
  };

  const openEod = () => {
    setEodOpen(true);
    loadEod(eodDate);
  };

  const handleEodPayChange = (orderId, val) => {
    setEodEdits((prev) => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        payment_method: val,
        dirty: val !== eodOrders.find((o) => o.order_id === orderId)?.payment_method,
      },
    }));
  };

  const handleEodNote = (orderId, val) => {
    setEodEdits((prev) => ({ ...prev, [orderId]: { ...prev[orderId], note: val } }));
  };

  // Lưu 1 đơn
  const saveEodOne = async (orderId) => {
    const edit = eodEdits[orderId];
    if (!edit?.dirty) return;
    setEodSaving((prev) => ({ ...prev, [orderId]: true }));
    try {
      await api.patch(`/orders/${orderId}/payment`, {
        payment_method: edit.payment_method,
        note: edit.note || "",
      });
      setEodOrders((prev) => prev.map((o) => o.order_id === orderId ? { ...o, payment_method: edit.payment_method } : o));
      setEodEdits((prev) => ({ ...prev, [orderId]: { ...prev[orderId], dirty: false } }));
      toast({ title: "Đã cập nhật", variant: "success" });
    } catch (e) {
      toast({ title: parseApiError(e, "Lỗi lưu"), variant: "error" });
    } finally {
      setEodSaving((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  // Lưu tất cả đơn có thay đổi
  const saveEodAll = async () => {
    const dirty = Object.entries(eodEdits).filter(([, v]) => v.dirty);
    if (dirty.length === 0) { toast({ title: "Không có thay đổi nào", variant: "info" }); return; }
    for (const [orderId] of dirty) await saveEodOne(orderId);
    toast({ title: `Đã lưu ${dirty.length} đơn`, variant: "success" });
    load(); // refresh bảng chính
  };

  const eodDirtyCount = Object.values(eodEdits).filter((v) => v.dirty).length;

  const { sorted: sortedOrders, handleSort, SortIcon } = useSort(orders, "created_at", "desc");

  const load = async () => {
    setLoading(true);
    const params = { sort, direction };
    if (q) params.q = q;
    if (status) params.status = status;
    if (payment) params.payment_method = payment;
    if (type) params.type = type;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    try {
      const { data } = await api.get("/orders", { params });
      setOrders(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, payment, type, dateFrom, dateTo, sort, direction]);

  const filterLabel = useMemo(() => {
    const parts = [];
    if (status) parts.push(t(`status.${status}`));
    if (payment) parts.push(t(`payment.${payment}`));
    if (type) parts.push(t(`orders.${type}`));
    if (dateFrom || dateTo) parts.push(`${dateFrom || "..."} → ${dateTo || "..."}`);
    if (q) parts.push(`"${q}"`);
    return parts.join(" · ");
  }, [status, payment, type, dateFrom, dateTo, q, t]);

  const totalRevenue = orders.reduce((s, o) => s + (o.total || 0), 0);

  const duplicate = async (orderId) => {
    navigate(`/orders/new?clone_from=${orderId}`);
  };

  const submitDeleteAll = async (e) => {
    e?.preventDefault?.();
    if (delAllForm.confirm_text !== "XOA TAT CA") {
      toast({ title: "Cần gõ chính xác chuỗi XOA TAT CA", variant: "error" });
      return;
    }
    if (!delAllForm.account_password || !delAllForm.pin_a || !delAllForm.pin_b) {
      toast({ title: "Cần nhập đủ 3 mật khẩu", variant: "error" });
      return;
    }
    setDelAllBusy(true);
    try {
      const { data } = await api.post("/orders/delete-all", {
        account_password: delAllForm.account_password,
        pin_a: delAllForm.pin_a,
        pin_b: delAllForm.pin_b,
      });
      toast({ title: `Đã xóa ${data.deleted} đơn`, variant: "success" });
      setDelAllOpen(false);
      setDelAllForm({ account_password: "", pin_a: "", pin_b: "", confirm_text: "" });
      load();
    } catch (err) {
      toast({ title: parseApiError(err, "Lỗi"), variant: "error" });
    } finally {
      setDelAllBusy(false);
    }
  };

  const handleQuickRange = (key) => {
    setQuickRange(key);
    if (key !== "custom") {
      const { dateFrom: df, dateTo: dt } = getQuickRange(key);
      setDateFrom(df);
      setDateTo(dt);
    }
  };

  const reset = () => {
    setQ(""); setStatus(""); setPayment(""); setType("");
    setDateFrom(""); setDateTo(""); setQuickRange("");
    setSort("created_at"); setDirection("desc");
  };

  // Đổi trạng thái inline trực tiếp từ danh sách
  const [changingStatus, setChangingStatus] = useState(null); // order_id đang đổi
  const STATUS_OPTIONS = [
    { value: "new",          label: t("status.new") },
    { value: "processing",   label: t("status.processing") },
    { value: "delivering",   label: t("status.delivering") },
    { value: "delivered",    label: t("status.delivered") },
    { value: "debt_pending", label: t("status.debt_pending") },
    { value: "cancelled",    label: t("status.cancelled") },
  ];

  const changeStatus = async (orderId, newStatus) => {
    if (!newStatus) return;
    setChangingStatus(orderId);
    try {
      await api.put(`/orders/${orderId}/status`, { status: newStatus });
      setOrders((prev) => prev.map((o) => o.order_id === orderId ? { ...o, status: newStatus } : o));
      toast({ title: "Đã cập nhật trạng thái", variant: "success" });
    } catch (err) {
      toast({ title: parseApiError(err) || t("common.error"), variant: "error" });
    } finally {
      setChangingStatus(null);
    }
  };


  const handlePrint = () => {
    const PAY = {debt:'Công nợ',cash:'Tiền mặt',transfer:'Chuyển khoản',ewallet:'Ví điện tử',card:'Thẻ'};
    const STATUS = {new:'Mới',processing:'Đang xử lý',delivering:'Đang giao',delivered:'Đã giao',cancelled:'Đã hủy',debt_pending:'Chờ thu nợ'};
    const rows = sortedOrders.map((o,i) => `<tr>
      <td>${i+1}</td><td>${o.order_code||''}</td>
      <td>${o.customer_name||''}<br/><span style="color:#888;font-size:10px">${o.customer_phone||''}</span></td>
      <td>${o.type==='wholesale'?'Sỉ':o.type==='delivery'?'Giao':'Lẻ'}</td>
      <td class="r">${(o.total||0).toLocaleString('vi-VN')}₫</td>
      <td>${PAY[o.payment_method]||o.payment_method||''}</td>
      <td>${STATUS[o.status]||o.status||''}</td>
      <td>${o.created_at?o.created_at.slice(0,10):''}</td>
    </tr>`).join('');
    const total = sortedOrders.reduce((s,o)=>s+(o.total||0),0);
    const win = window.open('','_blank');
    win.document.write(`<!DOCTYPE html><html lang="vi"><head><meta charset="UTF-8"/>
    <title>Danh sách đơn hàng</title>
    <style>body{font-family:Arial,sans-serif;font-size:12px;margin:20px}h2{margin:0 0 4px}p.sub{color:#555;font-size:11px;margin:0 0 12px}
    table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:5px 8px;text-align:left;vertical-align:top}
    th{background:#f0f0f0;font-size:11px;text-transform:uppercase}tfoot td{font-weight:bold;background:#e8f5e9}
    .r{text-align:right;font-family:monospace;white-space:nowrap}tr:nth-child(even) td{background:#fafafa}
    @media print{body{margin:10mm}}</style></head><body>
    <h2>Danh sách đơn hàng</h2>
    <p class="sub">${sortedOrders.length} đơn · Tổng: ${total.toLocaleString('vi-VN')}₫</p>
    <table><thead><tr><th>STT</th><th>Mã đơn</th><th>Khách hàng</th><th>Loại</th><th class="r">Tổng tiền</th><th>Thanh toán</th><th>Trạng thái</th><th>Ngày</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td colspan="4" style="text-align:right">TỔNG CỘNG</td><td class="r">${total.toLocaleString('vi-VN')}₫</td><td colspan="3"></td></tr></tfoot>
    </table><script>window.onload=()=>{window.print()}<\/script></body></html>`);
    win.document.close();
  };
  return (
    <div className="space-y-6 animate-fade-in" data-testid="orders-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t("orders.title")}</h1>
          <p className="text-sm text-ink-secondary mt-1">
            {orders.length} đơn · Doanh thu: <span className="font-medium text-bamboo">{formatVND(totalRevenue)}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canPrint && (
            <Button variant="outline" size="md" onClick={handlePrint} data-testid="orders-print-button">
              <Printer size={14} /> In / PDF
            </Button>
          )}
          <Button
            variant="outline" size="md"
            onClick={openEod}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="orders-eod-button"
            title="Cập nhật hình thức thanh toán hàng loạt cuối ngày"
          >
            <ClipboardCheck size={14} /> Chốt cuối ngày
          </Button>
          {user?.role === "admin" && canDelete && (
            <Button variant="outline" size="md" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => setDelAllOpen(true)} data-testid="orders-delete-all-button">
              <Trash size={14} /> Xóa toàn bộ
            </Button>
          )}
          <Link to="/orders/new">
            <Button data-testid="orders-new-button">
              <Plus size={16} />{t("orders.new")}
            </Button>
          </Link>
        </div>
      </div>

      <Modal open={delAllOpen} onClose={() => setDelAllOpen(false)} title="Xóa toàn bộ đơn hàng" size="md" testId="orders-delete-all-modal">
        <form onSubmit={submitDeleteAll} className="space-y-3">
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5">
            ⚠ Thao tác này sẽ <strong>xóa toàn bộ đơn hàng</strong> và khôi phục lại tồn kho. Yêu cầu 3 lớp xác thực + chuỗi xác nhận. Không thể hoàn tác.
          </div>
          <div>
            <Label>1) Mật khẩu tài khoản admin</Label>
            <div className="relative">
              <Input type={showDelPw.acc ? "text" : "password"} value={delAllForm.account_password}
                onChange={(e) => setDelAllForm({ ...delAllForm, account_password: e.target.value })}
                required className="pr-9" data-testid="del-all-account-pass" />
              <button type="button" onClick={() => setShowDelPw(p => ({ ...p, acc: !p.acc }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition-colors" tabIndex={-1}>
                {showDelPw.acc ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <div>
            <Label>2) Mật khẩu xóa A</Label>
            <div className="relative">
              <Input type={showDelPw.a ? "text" : "password"} value={delAllForm.pin_a}
                onChange={(e) => setDelAllForm({ ...delAllForm, pin_a: e.target.value })}
                required className="pr-9" data-testid="del-all-pin-a" />
              <button type="button" onClick={() => setShowDelPw(p => ({ ...p, a: !p.a }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition-colors" tabIndex={-1}>
                {showDelPw.a ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <div>
            <Label>3) Mật khẩu xóa B</Label>
            <div className="relative">
              <Input type={showDelPw.b ? "text" : "password"} value={delAllForm.pin_b}
                onChange={(e) => setDelAllForm({ ...delAllForm, pin_b: e.target.value })}
                required className="pr-9" data-testid="del-all-pin-b" />
              <button type="button" onClick={() => setShowDelPw(p => ({ ...p, b: !p.b }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition-colors" tabIndex={-1}>
                {showDelPw.b ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          <div>
            <Label>Gõ <span className="font-mono font-bold">XOA TAT CA</span> để xác nhận</Label>
            <Input
              value={delAllForm.confirm_text}
              onChange={(e) => setDelAllForm({ ...delAllForm, confirm_text: e.target.value })}
              data-testid="del-all-confirm-text"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setDelAllOpen(false)}>Hủy</Button>
            <Button type="submit" disabled={delAllBusy} className="bg-red-600 hover:bg-red-700" data-testid="del-all-submit">
              {delAllBusy ? "..." : "XÓA TOÀN BỘ"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* THANH XEM NHANH THEO THỜI GIAN */}
      <div className="flex flex-wrap items-center gap-2" data-testid="orders-quick-range">
        <div className="flex items-center gap-1.5 text-xs text-ink-muted font-medium mr-1">
          <CalendarDays size={14} />
          <span>Xem nhanh:</span>
        </div>
        {[
          { key: "today",  label: "Hôm nay" },
          { key: "week",   label: "Tuần này" },
          { key: "month",  label: "Tháng này" },
          { key: "year",   label: "Năm nay" },
          { key: "custom", label: "Tuỳ chọn" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => handleQuickRange(key)}
            className={[
              "px-3 py-1.5 rounded-full text-xs font-semibold border transition-all",
              quickRange === key
                ? "bg-bamboo text-white border-bamboo shadow-sm"
                : "bg-white text-ink-secondary border-border hover:border-bamboo hover:text-bamboo"
            ].join(" ")}
            data-testid={`orders-quick-${key}`}
          >
            {label}
          </button>
        ))}
        {quickRange && (
          <button
            onClick={() => { setQuickRange(""); setDateFrom(""); setDateTo(""); }}
            className="px-2 py-1 rounded-full text-xs text-ink-muted hover:text-terracotta transition-colors"
            title="Xóa lọc nhanh"
          >
            ✕ Bỏ lọc
          </button>
        )}
        {(dateFrom || dateTo) && (
          <span className="ml-auto text-xs text-ink-muted bg-cream px-3 py-1 rounded-full border border-border">
            📅 {dateFrom || "..."} → {dateTo || "..."}
          </span>
        )}
      </div>

      <Card>
        <CardContent>
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="grid grid-cols-1 sm:grid-cols-8 gap-3" data-testid="orders-filters">
            <div className="sm:col-span-2">
              <Label>{t("common.search")}</Label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`${t("orders.code")} / ${t("orders.customer")}`} className="pl-8" data-testid="orders-search-input" />
              </div>
            </div>
            {/* Chỉ hiện ô ngày khi chọn Tuỳ chọn hoặc chưa chọn gì */}
            {(quickRange === "custom" || quickRange === "") && (<>
            <div>
              <Label>Từ ngày</Label>
              <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setQuickRange("custom"); }} data-testid="orders-date-from" />
            </div>
            <div>
              <Label>Đến ngày</Label>
              <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setQuickRange("custom"); }} data-testid="orders-date-to" />
            </div>
            </>)}
            <div>
              <Label>{t("common.status")}</Label>
              <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="orders-status-filter">
                <option value="">{t("common.all")}</option>
                <option value="new">{t("status.new")}</option>
                <option value="processing">{t("status.processing")}</option>
                <option value="delivering">{t("status.delivering")}</option>
                <option value="delivered">{t("status.delivered")}</option>
                <option value="debt_pending">{t("status.debt_pending")}</option>
                <option value="cancelled">{t("status.cancelled")}</option>
              </Select>
            </div>
            <div>
              <Label>{t("orders.payment")}</Label>
              <Select value={payment} onChange={(e) => setPayment(e.target.value)} data-testid="orders-payment-filter">
                <option value="">{t("common.all")}</option>
                <option value="cash">{t("payment.cash")}</option>
                <option value="transfer">{t("payment.transfer")}</option>
                <option value="debt">{t("payment.debt")}</option>
                <option value="ewallet">{t("payment.ewallet")}</option>
                <option value="card">{t("payment.card")}</option>
              </Select>
            </div>
            <div>
              <Label>{t("orders.type")}</Label>
              <Select value={type} onChange={(e) => setType(e.target.value)} data-testid="orders-type-filter">
                <option value="">{t("common.all")}</option>
                <option value="retail">{t("orders.retail")}</option>
                <option value="wholesale">{t("orders.wholesale")}</option>
                <option value="delivery">{t("orders.delivery")}</option>
              </Select>
            </div>
            <div>
              <Label>{t("common.sort")}</Label>
              <div className="flex gap-1">
                <Select value={sort} onChange={(e) => setSort(e.target.value)} className="flex-1 text-xs" data-testid="orders-sort-field">
                  <option value="created_at">Ngày</option>
                  <option value="total">Tổng tiền</option>
                  <option value="customer_name">Tên KH</option>
                </Select>
                <Button type="button" size="sm" variant="outline" onClick={() => setDirection(direction === "asc" ? "desc" : "asc")} data-testid="orders-sort-direction" title={direction === "asc" ? "Tăng" : "Giảm"}>
                  {direction === "asc" ? "↑" : "↓"}
                </Button>
              </div>
            </div>
            <div className="sm:col-span-8 flex gap-2 justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={reset} data-testid="orders-filters-reset">Xóa lọc</Button>
              <Button type="submit" size="sm" data-testid="orders-filters-apply">{t("common.filter")}</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
              <tr>
                <th className="text-left py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSort("order_code")}>{t("orders.code")}{SortIcon("order_code")}</th>
                <th className="text-left py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSort("customer_name")}>{t("orders.customer")}{SortIcon("customer_name")}</th>
                <th className="text-center py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSort("type")}>{t("orders.type")}{SortIcon("type")}</th>
                <th className="text-right py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSort("total")}>{t("orders.total")}{SortIcon("total")}</th>
                <th className="text-center py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSort("payment_method")}>{t("orders.payment")}{SortIcon("payment_method")}</th>
                <th className="text-center py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSort("status")}>{t("common.status")}{SortIcon("status")}</th>
                <th className="text-left py-3 px-4 cursor-pointer select-none hover:text-ink" onClick={() => handleSort("created_at")}>{t("orders.date")}{SortIcon("created_at")}</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody data-testid="orders-table-body">
              {loading ? (
                <TableRowsSkeleton rows={6} cols={8} />
              ) : orders.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-ink-muted">{t("common.empty")}</td></tr>
              ) : (
                sortedOrders.map((o) => (
                  <tr key={o.order_id} className="border-t border-border hover:bg-cream/30" data-testid={`order-row-${o.order_id}`}>
                    <td className="py-3 px-4 font-mono text-xs">{o.order_code}</td>
                    <td className="py-3 px-4">
                      <div className="font-medium">{o.customer_name}</div>
                      <div className="text-xs text-ink-muted">{o.customer_phone}</div>
                    </td>
                    <td className="py-3 px-4 text-center text-xs">{t(`orders.${o.type || "retail"}`)}</td>
                    <td className="py-3 px-4 text-right font-medium">{formatVND(o.total)}</td>
                    <td className="py-3 px-4 text-center text-xs">{t(`payment.${o.payment_method}`)}</td>
                    <td className="py-3 px-4 text-center">
                      <div className="relative inline-flex items-center group">
                        <select
                          value={o.status}
                          disabled={changingStatus === o.order_id}
                          onChange={(e) => changeStatus(o.order_id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`order-status-select-${o.order_id}`}
                          className={[
                            "appearance-none pl-2 pr-6 py-1 rounded-full text-xs font-semibold border cursor-pointer transition-all outline-none",
                            "focus:ring-2 focus:ring-bamboo/40 disabled:opacity-50 disabled:cursor-wait",
                            o.status === "delivered"    ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                            o.status === "delivering"   ? "bg-blue-100 text-blue-800 border-blue-300" :
                            o.status === "processing"   ? "bg-amber-100 text-amber-800 border-amber-300" :
                            o.status === "new"          ? "bg-gray-100 text-gray-700 border-gray-300" :
                            o.status === "debt_pending" ? "bg-orange-100 text-orange-800 border-orange-300" :
                            o.status === "cancelled"    ? "bg-red-100 text-red-700 border-red-300" :
                                                          "bg-gray-100 text-gray-700 border-gray-300"
                          ].join(" ")}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                        <ChevronDown
                          size={10}
                          className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 opacity-60"
                        />
                      </div>
                    </td>
                    <td className="py-3 px-4 text-xs text-ink-muted">{formatDateTime(o.created_at)}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex gap-1 justify-end">
                        {canDuplicate && (
                          <Button variant="ghost" size="icon" onClick={() => duplicate(o.order_id)} title="Nhân bản" data-testid={`order-duplicate-${o.order_id}`}>
                            <Copy size={14} />
                          </Button>
                        )}
                        {canEdit && !["delivered","cancelled"].includes(o.status) && (
                          <Link to={`/orders/${o.order_id}`} state={{ openEdit: true }}>
                            <Button variant="ghost" size="icon" title="Chỉnh sửa" data-testid={`order-edit-${o.order_id}`}>
                              <Edit3 size={14} />
                            </Button>
                          </Link>
                        )}
                        <Link to={`/orders/${o.order_id}`}>
                          <Button variant="ghost" size="icon" data-testid={`order-view-${o.order_id}`}>
                            <Eye size={14} />
                          </Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <PrintPreview
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        title="Danh sách đơn hàng"
        printId="orders-list-print"
        pdfFilename={`danh-sach-don-hang-${new Date().toISOString().slice(0, 10)}.pdf`}
        pdfFormat="a4"
      >
        <OrdersListPrint orders={orders} filterLabel={filterLabel} />
      </PrintPreview>

      {/* ── Modal chốt cuối ngày — render qua Portal để thoát overflow của bảng ── */}
      {eodOpen && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setEodOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-border w-full max-w-4xl max-h-[88vh] flex flex-col">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0 bg-amber-50 rounded-t-2xl">
              <div>
                <h2 className="font-heading font-bold text-lg flex items-center gap-2 text-amber-800">
                  <ClipboardCheck size={20} className="text-amber-600" /> Chốt cuối ngày — Cập nhật hình thức thanh toán
                </h2>
                <p className="text-xs text-amber-700/70 mt-0.5">
                  Cập nhật hàng loạt cho các đơn khách đổi hình thức thanh toán. Thay đổi được ghi vào timeline đơn.
                </p>
              </div>
              <button onClick={() => setEodOpen(false)} className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-surface-hover">
                ✕
              </button>
            </div>

            {/* Chọn ngày + actions */}
            <div className="flex items-center gap-3 px-6 py-3 border-b border-border/60 bg-cream/30 shrink-0 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="shrink-0 text-xs">Ngày:</Label>
                <input
                  type="date"
                  value={eodDate}
                  onChange={(e) => { setEodDate(e.target.value); loadEod(e.target.value); }}
                  className="h-8 px-2 text-sm border border-border rounded-md bg-white"
                />
                <Button size="sm" variant="ghost" onClick={() => loadEod(eodDate)} title="Tải lại">
                  <RefreshCw size={13} />
                </Button>
              </div>
              <div className="flex-1" />
              {eodDirtyCount > 0 && (
                <span className="text-xs text-amber-700 font-semibold bg-amber-100 px-2 py-1 rounded-full">
                  {eodDirtyCount} đơn có thay đổi chưa lưu
                </span>
              )}
              <Button
                size="sm"
                onClick={saveEodAll}
                disabled={eodDirtyCount === 0}
                className={eodDirtyCount > 0 ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}
              >
                <ClipboardCheck size={13} /> Lưu tất cả ({eodDirtyCount})
              </Button>
            </div>

            {/* Bảng đơn */}
            <div className="overflow-y-auto flex-1">
              {eodLoading ? (
                <div className="py-16 text-center text-sm text-ink-muted">Đang tải đơn hàng...</div>
              ) : eodOrders.length === 0 ? (
                <div className="py-16 text-center text-sm text-ink-muted">Không có đơn nào trong ngày này</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white border-b border-border text-[11px] uppercase text-ink-muted">
                    <tr>
                      <th className="text-left py-2.5 px-4">Mã đơn</th>
                      <th className="text-left py-2.5 px-4">Khách hàng</th>
                      <th className="text-left py-2.5 px-4">Hàng hóa</th>
                      <th className="text-right py-2.5 px-4">Tổng tiền</th>
                      <th className="text-center py-2.5 px-4">Trạng thái</th>
                      <th className="text-center py-2.5 px-4 font-bold text-amber-700">HTTT hiện tại → mới</th>
                      <th className="py-2.5 px-4 text-center">Lưu</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {eodOrders.map((o) => {
                      const edit      = eodEdits[o.order_id] || { payment_method: o.payment_method, dirty: false };
                      const saving    = eodSaving[o.order_id];
                      const changed   = edit.dirty;
                      const noteOpen  = eodNoteOpen === o.order_id;

                      return (
                        <React.Fragment key={o.order_id}>
                          <tr className={changed ? "bg-amber-50/60" : "hover:bg-cream/20"}>
                            {/* Mã đơn */}
                            <td className="py-2.5 px-4">
                              <span className="font-mono text-xs font-semibold text-bamboo">{o.order_code}</span>
                              <div className="text-[10px] text-ink-muted">{formatDateTime(o.created_at)}</div>
                            </td>

                            {/* Khách */}
                            <td className="py-2.5 px-4">
                              <div className="font-medium text-sm">{o.customer_name || "—"}</div>
                              <div className="text-[10px] text-ink-muted">{o.customer_phone}</div>
                            </td>

                            {/* Hàng hóa */}
                            <td className="py-2.5 px-4 max-w-[200px]">
                              <div className="flex flex-wrap gap-1">
                                {(o.items || []).slice(0, 3).map((it, i) => (
                                  <span key={i} className="text-[10px] bg-cream px-1.5 py-0.5 rounded">
                                    {it.name} ×{it.quantity}
                                  </span>
                                ))}
                                {(o.items || []).length > 3 && (
                                  <span className="text-[10px] text-ink-muted">+{o.items.length - 3}</span>
                                )}
                              </div>
                            </td>

                            {/* Tổng tiền */}
                            <td className="py-2.5 px-4 text-right font-mono font-semibold text-bamboo">
                              {formatVND(o.total)}
                            </td>

                            {/* Trạng thái */}
                            <td className="py-2.5 px-4 text-center">
                              <Badge variant={
                                o.status === "delivered" ? "delivered"
                                : o.status === "delivering" ? "processing"
                                : o.status === "cancelled" ? "cancelled"
                                : "regular"
                              }>
                                {t(`status.${o.status}`) || o.status}
                              </Badge>
                            </td>

                            {/* HTTT hiện tại → dropdown mới */}
                            <td className="py-2.5 px-4">
                              <div className="flex items-center gap-2 justify-center">
                                {/* Cũ */}
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                  o.payment_method === "debt" ? "bg-red-100 text-red-700"
                                  : o.payment_method === "cash" ? "bg-green-100 text-green-700"
                                  : "bg-blue-100 text-blue-700"
                                }`}>
                                  {PAY_LABEL[o.payment_method] || o.payment_method}
                                </span>

                                {changed && <span className="text-ink-muted text-xs">→</span>}

                                {/* Dropdown mới */}
                                <select
                                  value={edit.payment_method}
                                  onChange={(e) => handleEodPayChange(o.order_id, e.target.value)}
                                  className={`h-7 text-xs px-2 rounded-md border focus:outline-none focus:ring-1 focus:ring-amber-400 ${
                                    changed ? "border-amber-400 bg-amber-50 font-semibold text-amber-800" : "border-border bg-white text-ink"
                                  }`}
                                >
                                  {PAY_OPTIONS.map(([val, lbl]) => (
                                    <option key={val} value={val}>{lbl}</option>
                                  ))}
                                </select>

                                {/* Nút ghi chú */}
                                <button
                                  onClick={() => setEodNoteOpen(noteOpen ? null : o.order_id)}
                                  className="text-[10px] text-ink-muted hover:text-ink px-1 py-0.5 rounded border border-dashed border-border hover:border-solid"
                                  title="Thêm ghi chú"
                                >
                                  {edit.note ? "📝" : "ghi chú"}
                                </button>
                              </div>

                              {/* Ô ghi chú inline */}
                              {noteOpen && (
                                <div className="mt-1.5 flex gap-1">
                                  <input
                                    autoFocus
                                    type="text"
                                    placeholder="Lý do đổi HTTT..."
                                    value={edit.note}
                                    onChange={(e) => handleEodNote(o.order_id, e.target.value)}
                                    className="flex-1 h-7 px-2 text-xs border border-amber-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-400"
                                    onKeyDown={(e) => e.key === "Enter" && setEodNoteOpen(null)}
                                  />
                                  <button onClick={() => setEodNoteOpen(null)} className="text-xs text-ink-muted hover:text-ink px-1">✓</button>
                                </div>
                              )}
                            </td>

                            {/* Nút lưu riêng */}
                            <td className="py-2.5 px-4 text-center">
                              {changed ? (
                                <Button
                                  size="sm"
                                  onClick={() => saveEodOne(o.order_id)}
                                  disabled={saving}
                                  className="text-xs bg-amber-600 hover:bg-amber-700 text-white h-7 px-2"
                                >
                                  {saving ? "..." : "Lưu"}
                                </Button>
                              ) : (
                                <span className="text-[10px] text-green-600">✓</span>
                              )}
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  {/* Footer tóm tắt */}
                  <tfoot className="border-t-2 border-border bg-cream/40 text-xs sticky bottom-0">
                    <tr>
                      <td colSpan={3} className="py-2 px-4 text-ink-muted">
                        {eodOrders.length} đơn · {eodDirtyCount > 0
                          ? <span className="text-amber-700 font-semibold">{eodDirtyCount} đơn cần lưu</span>
                          : <span className="text-green-700">Tất cả đã đồng bộ ✓</span>}
                      </td>
                      <td className="py-2 px-4 text-right font-mono font-semibold">
                        {formatVND(eodOrders.reduce((s, o) => s + (o.total || 0), 0))}
                      </td>
                      <td colSpan={3} className="py-2 px-4 text-right">
                        <Button size="sm" onClick={saveEodAll} disabled={eodDirtyCount === 0}
                          className={eodDirtyCount > 0 ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}>
                          <ClipboardCheck size={12} /> Lưu tất cả ({eodDirtyCount})
                        </Button>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}